import type { ColumnDef } from '@tanstack/react-table';
import { addMonths, addYears, format, isAfter, isBefore, isValid, parse, subYears } from 'date-fns';
import { ArrowUpDown, Pencil, Trash } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { Event } from '@/graphql/types';

interface EditableCellProps<TData> {
  value: string;
  row: { index: number; original: TData };
  column: { id: string };
  table: {
    options: {
      meta: {
        updateData: (rowIndex: number, columnId: string, value: string) => void;
      };
    };
  };
}

export const EditableCell = <TData,>({ value, row, column, table }: EditableCellProps<TData>) => {
  const [editedValue, setEditedValue] = useState(value);
  const { updateData } = table.options.meta;

  useEffect(() => {
    setEditedValue(value);
  }, [value]);

  const onBlur = () => {
    updateData(row.index, column.id, editedValue);
  };

  return (
    <Input value={editedValue} onChange={(e) => setEditedValue(e.target.value)} onBlur={onBlur} />
  );
};

const StartDateCell = ({ editForm }: { editForm: UseFormReturn<Event> }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dateRef = useRef<string | null | undefined>(null);

  useEffect(() => {
    const startDate = editForm.getValues('start_date');
    const endDate = editForm.getValues('end_date');

    if (startDate) {
      const startDateObj = parse(startDate, 'yyyy-MM-dd', new Date());
      const oneMonthLater = addMonths(startDateObj, 1);

      let newEndDate = endDate;

      if (!endDate || isBefore(parse(endDate, 'yyyy-MM-dd', new Date()), startDateObj)) {
        newEndDate = startDate;
      } else if (isAfter(parse(endDate, 'yyyy-MM-dd', new Date()), oneMonthLater)) {
        newEndDate = format(oneMonthLater, 'yyyy-MM-dd');
      }

      if (newEndDate !== endDate) {
        editForm.setValue('end_date', newEndDate);
      }
    }
  }, [editForm.watch('start_date')]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const formValue = editForm.watch('start_date');
    if (!formValue) {
      setDateInput('');
      dateRef.current = null;
    }
  }, [editForm]);

  const handleDateInput = (dateString: string, onChange: (date: string | null) => void) => {
    setDateInput(dateString);
    if (dateString === '') {
      onChange(null);
      dateRef.current = null;
    } else if (isValid(parse(dateString, 'yyyy-MM-dd', new Date()))) {
      onChange(dateString);
      dateRef.current = dateString;
      editForm.trigger('start_date');
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (isValid(parse(dateInput, 'yyyy-MM-dd', new Date()))) {
        setIsOpen(false);
      }
    }
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <Form {...editForm}>
      <FormField
        control={editForm.control}
        name='start_date'
        render={({ field }) => (
          <FormItem className='flex flex-col'>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    ref={buttonRef}
                    variant='outline'
                    role='combobox'
                    aria-expanded={isOpen}
                    aria-haspopup='dialog'
                    aria-label='Select Start Date'
                    className={`w-[240px] pl-3 text-left font-normal ${
                      !field.value && 'text-muted-foreground'
                    }`}
                    onClick={() => {
                      setIsOpen(true);
                      if (dateRef.current) {
                        field.onChange(dateRef.current);
                      }
                    }}
                    onKeyDown={handleButtonKeyDown}
                  >
                    {field.value ? (
                      format(parse(field.value, 'yyyy-MM-dd', new Date()), 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0' align='start'>
                <div className='p-2'>
                  <Input
                    type='date'
                    ref={inputRef}
                    id='start_date'
                    value={dateInput}
                    onChange={(e) => handleDateInput(e.target.value, field.onChange)}
                    onKeyDown={handleInputKeyDown}
                    className='mb-2'
                    aria-label='Enter Start Date'
                  />
                </div>
                <Calendar
                  mode='single'
                  fixedWeeks={true}
                  defaultMonth={parse(field.value, 'yyyy-MM-dd', new Date())}
                  startMonth={subYears(new Date(), 10)}
                  endMonth={addYears(new Date(), 5)}
                  selected={field.value ? parse(field.value, 'yyyy-MM-dd', new Date()) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      const dateString = format(date, 'yyyy-MM-dd');
                      field.onChange(dateString);
                      dateRef.current = dateString;
                      setIsOpen(false);
                      setDateInput(dateString);
                      buttonRef.current?.focus();
                    }
                  }}
                  className='rounded-md border shadow-sm w-64'
                  captionLayout='dropdown'
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
};

const EndDateCell = ({ editForm }: { editForm: UseFormReturn<Event> }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [localMinDate, setLocalMinDate] = useState<string | undefined>(undefined);
  const [localMaxDate, setLocalMaxDate] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dateRef = useRef<string | null | undefined>(null);

  const minDate = editForm.getValues('start_date');

  useEffect(() => {
    setLocalMinDate(minDate);
    if (minDate) {
      const oneMonthLater = format(
        addMonths(parse(minDate, 'yyyy-MM-dd', new Date()), 1),
        'yyyy-MM-dd',
      );
      setLocalMaxDate(oneMonthLater);

      const currentEndDate = editForm.getValues('end_date');
      if (
        currentEndDate &&
        (isBefore(
          parse(currentEndDate, 'yyyy-MM-dd', new Date()),
          parse(minDate, 'yyyy-MM-dd', new Date()),
        ) ||
          isAfter(
            parse(currentEndDate, 'yyyy-MM-dd', new Date()),
            parse(oneMonthLater, 'yyyy-MM-dd', new Date()),
          ))
      ) {
        editForm.setValue('end_date', '');
        setDateInput('');
      }
    }
  }, [minDate, editForm]);

  useEffect(() => {
    const formValue = editForm.watch('end_date');
    if (!formValue) {
      setDateInput('');
      dateRef.current = null;
    }
  }, [editForm]);

  const handleDateInput = (dateString: string, onChange: (date: string | null) => void) => {
    setDateInput(dateString);
    if (dateString === '') {
      onChange(null);
      dateRef.current = null;
    } else if (isValid(parse(dateString, 'yyyy-MM-dd', new Date()))) {
      if (localMinDate) {
        const oneMonthLater = format(
          addMonths(parse(localMinDate, 'yyyy-MM-dd', new Date()), 1),
          'yyyy-MM-dd',
        );
        if (
          isBefore(
            parse(dateString, 'yyyy-MM-dd', new Date()),
            parse(localMinDate, 'yyyy-MM-dd', new Date()),
          ) ||
          isAfter(
            parse(dateString, 'yyyy-MM-dd', new Date()),
            parse(oneMonthLater, 'yyyy-MM-dd', new Date()),
          )
        ) {
          return;
        }
      }
      onChange(dateString);
      dateRef.current = dateString;
      editForm.trigger('end_date');
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (isValid(parse(dateInput, 'yyyy-MM-dd', new Date()))) {
        setIsOpen(false);
      }
    }
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <Form {...editForm}>
      <FormField
        control={editForm.control}
        name='end_date'
        render={({ field }) => (
          <FormItem className='flex flex-col'>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    ref={buttonRef}
                    variant='outline'
                    role='combobox'
                    aria-expanded={isOpen}
                    aria-haspopup='dialog'
                    aria-label='Select End Date'
                    className={`w-[240px] pl-3 text-left font-normal ${
                      !field.value && 'text-muted-foreground'
                    }`}
                    onClick={() => {
                      setIsOpen(true);
                      if (dateRef.current) {
                        field.onChange(dateRef.current);
                      }
                    }}
                    onKeyDown={handleButtonKeyDown}
                  >
                    {field.value ? (
                      format(parse(field.value, 'yyyy-MM-dd', new Date()), 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0' align='start'>
                <div className='p-2'>
                  <Input
                    type='date'
                    ref={inputRef}
                    id='end_date'
                    value={dateInput}
                    onChange={(e) => handleDateInput(e.target.value, field.onChange)}
                    onKeyDown={handleInputKeyDown}
                    className='mb-2'
                    aria-label='Enter End Date'
                    min={localMinDate}
                    max={localMaxDate}
                  />
                </div>
                <Calendar
                  mode='single'
                  fixedWeeks={true}
                  defaultMonth={parse(field.value, 'yyyy-MM-dd', new Date())}
                  startMonth={subYears(new Date(), 10)}
                  endMonth={addYears(new Date(), 5)}
                  selected={field.value ? parse(field.value, 'yyyy-MM-dd', new Date()) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      const dateString = format(date, 'yyyy-MM-dd');
                      field.onChange(dateString);
                      dateRef.current = dateString;
                      setIsOpen(false);
                      setDateInput(dateString);
                      buttonRef.current?.focus();
                    }
                  }}
                  disabled={(date) => {
                    if (localMinDate) {
                      const oneMonthLater = addMonths(
                        parse(localMinDate, 'yyyy-MM-dd', new Date()),
                        1,
                      );
                      return (
                        isBefore(date, parse(localMinDate, 'yyyy-MM-dd', new Date())) ||
                        isAfter(date, oneMonthLater)
                      );
                    }
                    return localMinDate
                      ? date < parse(localMinDate, 'yyyy-MM-dd', new Date())
                      : date < new Date();
                  }}
                  className='rounded-md border shadow-sm w-64'
                  captionLayout='dropdown'
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
};

export const getEventColumns = (
  editingId: string | null,
  editForm: UseFormReturn<Event>,
  startEditing: (eventId: Event['id']) => void,
  saveEdit: (data: Event) => void,
  cancelEditing: () => void,
  deleteEvent: (eventId: Event['id']) => void,
  processing: boolean,
): ColumnDef<Event>[] => [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Name
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const event = row.original;
      if (editingId === event.id) {
        return (
          <EditableCell<Event>
            value={editForm.getValues('name') || ''}
            row={row}
            column={{ id: 'name' }}
            table={{
              options: {
                meta: {
                  updateData: (_rowIndex, _columnId, value) => {
                    editForm.setValue('name', value);
                  },
                },
              },
            }}
          />
        );
      }
      return event.name;
    },
  },
  {
    accessorKey: 'start_date',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Start Date
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const event = row.original;
      if (editingId === event.id) {
        return <StartDateCell editForm={editForm} />;
      }
      return format(parse(event.start_date, 'yyyy-MM-dd', new Date()), 'PPP');
    },
  },
  {
    accessorKey: 'end_date',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          End Date
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const event = row.original;
      if (editingId === event.id) {
        return <EndDateCell editForm={editForm} />;
      }
      return format(parse(event.end_date, 'yyyy-MM-dd', new Date()), 'PPP');
    },
  },
  {
    accessorKey: 'details',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Details
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const event = row.original;
      if (editingId === event.id) {
        return (
          <EditableCell<Event>
            value={editForm.getValues('details') || ''}
            row={row}
            column={{ id: 'details' }}
            table={{
              options: {
                meta: {
                  updateData: (_rowIndex, _columnId, value) => {
                    editForm.setValue('details', value);
                  },
                },
              },
            }}
          />
        );
      }
      return event.details;
    },
  },
  {
    accessorKey: 'created_by',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Created By
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const event = row.original;
      return event.created_by?.name;
    },
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Created
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const event = row.original;
      return event.created_at;
    },
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Updated
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const event = row.original;
      return event.updated_at;
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const event = row.original;
      if (editingId === event.id) {
        return (
          <>
            <Button
              disabled={processing}
              onClick={() => {
                editForm.handleSubmit(saveEdit)();
              }}
              className='mr-2'
            >
              {processing ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={cancelEditing} variant='outline'>
              Cancel
            </Button>
          </>
        );
      }
      return (
        <>
          <Button
            onClick={() => startEditing(event.id)}
            variant='ghost'
            size='icon'
            className='mr-2'
          >
            <Pencil className='h-4 w-4' />
          </Button>
          <Button onClick={() => deleteEvent(event.id)} variant='ghost' size='icon'>
            <Trash className='h-4 w-4' />
          </Button>
        </>
      );
    },
  },
];
